import './UserMenuButton.scss';

import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import Dotdotdot from 'react-dotdotdot';
import { Trans } from 'react-i18next';

import Button from '@/common/components/atoms/Button/Button';
import UserImage from '@/common/components/blocks/UserImage/UserImage';
import { useAuth } from '@/common/contexts/AuthContext';
import { useNotificationsContext } from '@/common/contexts/NotificationsContext/NotificationsContext';
import useBreakpoints from '@/common/hooks/useBreakpoints';
import useUserFilesAvatar from '@/common/hooks/users/files/useUserFilesAvatar';
import { ReactComponent as ArrowListSvg } from '@/ebat/assets/icons/triangle-bottom.svg';
import UserMenu, {
  getUserStore,
} from '@/ebat/components/blocks/Header/UserMenu/UserMenu';
import {
  MENU_NAME_USER,
  MENU_TYPE_DRAWER,
  MENU_TYPE_FIXED,
} from '@/ebat/contexts/Header/HeaderMenuContext/HeaderMenuContext';
import useHeaderMenuToggle from '@/ebat/hooks/header/useHeaderMenuToogle';

import Tooltip from '../../../Common/Tooltip/Tooltip';
import {
  UserMenuButtonDefaultProps,
  UserMenuButtonPropTypes,
} from './UserMenuButton.proptypes';

const UserMenuButton = ({ className }) => {
  const { user } = useAuth();
  const { data: avatar, isSuccess: isSuccessAvatar } = useUserFilesAvatar();
  const { allNotifications } = useNotificationsContext();
  const { isMinDesktop } = useBreakpoints();
  const userStore = getUserStore(user);
  const textRef = useRef(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      const element = textRef.current;

      // Vérifiez si le texte visible est plus court que le texte complet
      const visibleText = element.textContent.trim();
      setIsClamped(visibleText.length < userStore.length);
    }
  }, [userStore]);

  const {
    isOpen: isFixedMenuOpen,
    handleToggle: handleFixedMenuToggle,
  } = useHeaderMenuToggle(
    MENU_NAME_USER,
    MENU_TYPE_FIXED,
    'right',
    <UserMenu />,
  );
  const {
    isOpen: isDrawerOpen,
    handleToggle: handleDrawerToggle,
  } = useHeaderMenuToggle(
    MENU_NAME_USER,
    MENU_TYPE_DRAWER,
    'left',
    <UserMenu />,
  );
  const isOpen = isFixedMenuOpen || isDrawerOpen;

  const handleToggle = (e) => {
    if (isMinDesktop) {
      handleFixedMenuToggle(e);
    } else {
      handleDrawerToggle(e);
    }
  };

  return (
    <Button
      className={cn(className, 'user-menu-button', {
        'user-menu-button--open': isOpen,
      })}
      context="menu-btn-account"
      value={cn({ opened: isOpen }, { closed: !isOpen })}
      onClick={handleToggle}
    >
      <span className="user-menu-button__container">
        {allNotifications?.meta?.total_items_unread?.length > 0 && (
          <span className="user-menu-button__notifications" />
        )}
        {user && (
          <UserImage
            avatar={isSuccessAvatar ? avatar : ''}
            className="user-menu-button__avatar"
            color={user?.color}
            firstName={user?.first_name}
            identifier={user?.identifier}
            lastName={user?.last_name}
          />
        )}
        <span
          className="user-menu-button__label"
          data-qa-id="user-menu-button-label"
        >
          <Trans
            allowHTML
            components={{ span: <span /> }}
            i18nKey="ebat:entities:layout.header.my_account.account_span"
          />
          <div className="user-menu-button__warehouse">
            <span>
              {isClamped ? (
                <Tooltip
                  className="user-menu-button__tooltip"
                  message={userStore}
                  position="top"
                >
                  <Dotdotdot clamp={2}>
                    <span ref={textRef}>{userStore}</span>
                  </Dotdotdot>
                </Tooltip>
              ) : (
                <Dotdotdot clamp={2}>
                  <span ref={textRef}>{userStore}</span>
                </Dotdotdot>
              )}
            </span>
          </div>
        </span>
        <ArrowListSvg
          className={cn('user-menu-button__dropdown-icon', {
            'user-menu-button__dropdown-icon--open': isOpen,
          })}
        />
      </span>
    </Button>
  );
};

UserMenuButton.defaultProps = {
  ...UserMenuButtonDefaultProps,
};

UserMenuButton.propTypes = {
  ...UserMenuButtonPropTypes,
};

export default UserMenuButton;
